import React, { useState, useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import api from "../api/axios";
import { gradeFor } from "../components/ResultCardPreview";

/* ─── Design tokens ─────────────────────────────────── */
const C = {
  navy: "#1E3A5F",
  blue: "#2563EB",
  green: "#059669",
  amber: "#D97706",
  red: "#DC2626",
  bgGradient: "linear-gradient(135deg, #0f1e3c 0%, #1a3a6b 50%, #0f1e3c 100%)",
};

/* ─── Circular SVG progress indicator ───────────────── */
function CircularProgress({ percentage, size = 148, strokeWidth = 13 }) {
  const pct = Math.min(100, Math.max(0, percentage || 0));
  const color = pct >= 80 ? "#059669" : pct >= 60 ? "#2563EB" : pct >= 40 ? "#D97706" : "#DC2626";
  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference - (pct / 100) * circumference;
  return (
    <div style={{ position: "relative", width: size, height: size, flexShrink: 0 }}>
      <svg width={size} height={size} style={{ transform: "rotate(-90deg)", display: "block" }}>
        <circle cx={size / 2} cy={size / 2} r={radius} fill="none" stroke="#e2e8f0" strokeWidth={strokeWidth} />
        <circle cx={size / 2} cy={size / 2} r={radius} fill="none" stroke={color} strokeWidth={strokeWidth}
          strokeDasharray={circumference} strokeDashoffset={offset} strokeLinecap="round"
          style={{ transition: "stroke-dashoffset 1.2s ease" }} />
      </svg>
      <div style={{ position: "absolute", top: "50%", left: "50%", transform: "translate(-50%, -50%)", textAlign: "center" }}>
        <div style={{ fontSize: 28, fontWeight: 900, color, lineHeight: 1 }}>{pct}%</div>
        <div style={{ fontSize: 11, color: "#94a3b8", fontWeight: 600, marginTop: 3, letterSpacing: 0.3 }}>SCORE</div>
      </div>
    </div>
  );
}

/* ─── Mini stat card ─────────────────────────────────── */
function StatCard({ label, value, color, bg, icon }) {
  return (
    <div style={{ background: bg || "#f8fafc", borderRadius: 14, padding: "16px 14px", textAlign: "center", flex: 1, minWidth: 80 }}>
      {icon && <div style={{ fontSize: 20, marginBottom: 6 }}>{icon}</div>}
      <div style={{ fontSize: 11, fontWeight: 700, color: "#64748b", textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 5 }}>{label}</div>
      <div style={{ fontSize: 20, fontWeight: 900, color: color || C.navy }}>{value}</div>
    </div>
  );
}

/* ─── School Logo ─────────────────────────────────── */
function SchoolLogo({ logoData, size = 48 }) {
  if (logoData) return <img src={logoData} alt="School logo" style={{ height: size, width: size, objectFit: "contain", borderRadius: 8 }} />;
  return (
    <div style={{ height: size, width: size, background: "rgba(255,255,255,0.15)", borderRadius: 8, display: "flex", alignItems: "center", justifyContent: "center", fontSize: size * 0.55 }}>
      🏫
    </div>
  );
}

/* ─── Form field wrapper ────────────────────────────── */
function FormField({ label, children }) {
  return (
    <div style={{ marginBottom: 14 }}>
      <label style={{ display: "block", fontSize: 13, fontWeight: 600, color: "#374151", marginBottom: 5 }}>{label} *</label>
      {children}
    </div>
  );
}

const inputStyle = {
  width: "100%", padding: "10px 12px", border: "1.5px solid #d1d5db", borderRadius: 8,
  fontSize: 14, color: "#111827", background: "#fff", boxSizing: "border-box", outline: "none",
};

/* ─── Main Component ─────────────────────────────────── */
export default function StudentPortal() {
  const { schoolId: schoolIdParam } = useParams();
  const [step, setStep] = useState("form");
  const [form, setForm] = useState({ admissionNo: "", classSectionId: "" });
  const [classes, setClasses] = useState([]);
  const [token, setToken] = useState(null);
  const [studentName, setStudentName] = useState("");
  const [studentRollNo, setStudentRollNo] = useState("");
  const [studentAdmNo, setStudentAdmNo] = useState("");
  const [schoolInfo, setSchoolInfo] = useState(null);
  const [exams, setExams] = useState([]);
  const [result, setResult] = useState(null);
  const [selectedExam, setSelectedExam] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!schoolIdParam) return;
    api.get(`/portal/school?schoolId=${schoolIdParam}`).then((res) => setSchoolInfo(res.data)).catch(() => {});
    api.get(`/portal/classes?schoolId=${schoolIdParam}`).then((res) => setClasses(res.data)).catch(() => {});
  }, [schoolIdParam]);

  /* ── Lookup ──────────────────────────────────────── */
  async function handleLookup(e) {
    e.preventDefault();
    setError("");
    if (!schoolIdParam) { setError("No school ID in URL. Please use the link provided by your school."); return; }
    setLoading(true);
    try {
      const { data: lookupData } = await api.post("/portal/lookup", { ...form, schoolId: schoolIdParam });
      const tok = lookupData.token;
      setToken(tok);
      setStudentName(lookupData.studentName);
      setStudentRollNo(lookupData.rollNo || "");
      setStudentAdmNo(lookupData.admissionNo || "");

      const [schoolRes, examsRes] = await Promise.all([
        api.get(`/portal/school?schoolId=${schoolIdParam}`),
        api.get("/portal/results", { headers: { Authorization: `Bearer ${tok}` } }),
      ]);
      setSchoolInfo(schoolRes.data);
      setExams(examsRes.data);
      setStep("exams");
    } catch (err) {
      setError(err.response?.data?.error || "Could not find your record. Please check the details and try again.");
    } finally {
      setLoading(false);
    }
  }

  /* ── View result ─────────────────────────────────── */
  async function handleViewResult(exam) {
    setError("");
    setLoading(true);
    try {
      const { data } = await api.get(`/portal/results/${exam.id}`, { headers: { Authorization: `Bearer ${token}` } });
      setResult(data);
      setSelectedExam(exam);
      setStep("result");
    } catch (err) {
      setError(err.response?.data?.error || "Could not load result.");
    } finally {
      setLoading(false);
    }
  }

  function handleBack() { setResult(null); setSelectedExam(null); setError(""); setStep("exams"); }

  function handleSignOut() {
    setToken(null); setStudentName(""); setStudentRollNo(""); setStudentAdmNo("");
    setExams([]); setResult(null); setSelectedExam(null); setError("");
    setForm({ admissionNo: "", classSectionId: "" });
    setStep("form");
  }

  function openPortalPDF() {
    window.open(`/portal-print/${selectedExam.id}?token=${token}`, "_blank");
  }

  /* ════════════════════════════════════════════════════
     STEP: result — Engaging Dashboard
  ════════════════════════════════════════════════════ */
  if (step === "result" && result) {
    const scholastic = (result.subjects || []).filter((s) => s.category === "scholastic");
    const coscho = (result.subjects || []).filter((s) => s.category !== "scholastic");
    const passedCount = scholastic.filter((s) => s.passed).length;
    const hasAllMarks = scholastic.every((s) => s.isAbsent || (s.obtained !== null && s.obtained !== undefined));
    const pct = result.percentage || 0;
    const gradeColor = pct >= 80 ? C.green : pct >= 60 ? C.blue : pct >= 40 ? C.amber : C.red;

    return (
      <div style={{ minHeight: "100vh", background: C.bgGradient }}>
        <style>{`@media print { .no-print { display: none !important; } body { background: white !important; } }`}</style>

        {/* Top action bar */}
        <div className="no-print" style={{ background: "rgba(255,255,255,0.06)", borderBottom: "1px solid rgba(255,255,255,0.12)", padding: "12px 24px", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <SchoolLogo logoData={schoolInfo?.logoData} size={32} />
            <span style={{ color: "white", fontWeight: 700, fontSize: 15 }}>{schoolInfo?.displayName || schoolInfo?.name || "Student Portal"}</span>
          </div>
          <div style={{ display: "flex", gap: 10 }}>
            <button onClick={handleBack} style={{ background: "rgba(255,255,255,0.1)", color: "white", border: "1px solid rgba(255,255,255,0.25)", borderRadius: 8, padding: "7px 16px", fontSize: 13, fontWeight: 600, cursor: "pointer" }}>
              ← Back
            </button>
            <button onClick={openPortalPDF} style={{ background: "#F59E0B", color: "#000", border: "none", borderRadius: 8, padding: "7px 18px", fontSize: 13, fontWeight: 700, cursor: "pointer" }}>
              Download PDF (School Format)
            </button>
          </div>
        </div>

        <div style={{ maxWidth: 860, margin: "0 auto", padding: "28px 16px 48px" }}>

          {/* ── HERO CARD: Circular progress + student info ── */}
          <div style={{ background: "white", borderRadius: 20, boxShadow: "0 8px 40px rgba(0,0,0,0.22)", overflow: "hidden", marginBottom: 20 }}>
            {/* School header strip */}
            <div style={{ background: C.navy, padding: "18px 28px", display: "flex", alignItems: "center", gap: 16 }}>
              <SchoolLogo logoData={schoolInfo?.logoData} size={48} />
              <div>
                <div style={{ fontSize: 18, fontWeight: 800, color: "white", lineHeight: 1.2 }}>{schoolInfo?.displayName || schoolInfo?.name || "School"}</div>
                {schoolInfo?.board && <div style={{ fontSize: 12, color: "#93c5fd", marginTop: 3 }}>{schoolInfo.board}{schoolInfo.affiliationNo ? ` | Aff. No: ${schoolInfo.affiliationNo}` : ""}</div>}
              </div>
              <div style={{ marginLeft: "auto", textAlign: "right" }}>
                <div style={{ fontSize: 11, color: "rgba(255,255,255,0.6)", fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.5 }}>Exam</div>
                <div style={{ fontSize: 15, fontWeight: 800, color: "#F59E0B" }}>{selectedExam?.name}</div>
                <div style={{ fontSize: 12, color: "#93c5fd", marginTop: 2 }}>{selectedExam?.session}</div>
              </div>
            </div>

            {/* Dashboard body */}
            <div style={{ padding: "24px 28px", display: "flex", gap: 28, alignItems: "center", flexWrap: "wrap" }}>
              {/* Circular progress */}
              <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 12 }}>
                <CircularProgress percentage={pct} />
                <div style={{ textAlign: "center" }}>
                  <span style={{ display: "inline-block", padding: "4px 18px", borderRadius: 999, fontSize: 15, fontWeight: 800, background: hasAllMarks ? (result.overallPass ? "#d1fae5" : "#fee2e2") : "#f1f5f9", color: hasAllMarks ? (result.overallPass ? C.green : C.red) : "#64748b" }}>
                    {!hasAllMarks ? "Pending" : result.overallPass ? "PASS" : "FAIL"}
                  </span>
                </div>
              </div>

              {/* Student info */}
              <div style={{ flex: 1, minWidth: 220 }}>
                <div style={{ fontSize: 22, fontWeight: 900, color: C.navy, marginBottom: 12 }}>{studentName}</div>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "8px 24px" }}>
                  {[
                    { label: "Admission No.", value: studentAdmNo || "—" },
                    { label: "Roll Number", value: studentRollNo || "—" },
                    { label: "Grade", value: result.grade || "—" },
                    { label: "Subjects Passed", value: hasAllMarks ? `${passedCount} / ${scholastic.length}` : "—" },
                  ].map(({ label, value }) => (
                    <div key={label}>
                      <div style={{ fontSize: 10, fontWeight: 700, color: "#94a3b8", textTransform: "uppercase", letterSpacing: 0.4, marginBottom: 2 }}>{label}</div>
                      <div style={{ fontSize: 16, fontWeight: 700, color: C.navy }}>{value}</div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Grade badge */}
              <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 6 }}>
                <div style={{ width: 80, height: 80, borderRadius: "50%", background: gradeColor + "18", border: `3px solid ${gradeColor}`, display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <span style={{ fontSize: 36, fontWeight: 900, color: gradeColor }}>{result.grade}</span>
                </div>
                <div style={{ fontSize: 11, fontWeight: 700, color: "#64748b", textTransform: "uppercase", letterSpacing: 0.5 }}>Overall Grade</div>
              </div>
            </div>
          </div>

          {/* ── STATS ROW ── */}
          <div style={{ display: "flex", gap: 12, marginBottom: 20, flexWrap: "wrap" }}>
            <StatCard icon="📊" label="Total Marks" value={`${result.totalObtained} / ${result.totalMax}`} color={C.navy} bg="white" />
            <StatCard icon="📈" label="Percentage" value={`${pct}%`} color={gradeColor} bg="white" />
            <StatCard icon="✅" label="Subjects Passed" value={hasAllMarks ? `${passedCount}/${scholastic.length}` : "—"} color={passedCount === scholastic.length ? C.green : C.amber} bg="white" />
            <StatCard icon="🏆" label="Grade" value={result.grade || "—"} color={gradeColor} bg="white" />
          </div>

          {/* ── SUBJECT PERFORMANCE BARS ── */}
          {scholastic.length > 0 && (
            <div style={{ background: "white", borderRadius: 20, boxShadow: "0 4px 24px rgba(0,0,0,0.12)", padding: "22px 24px", marginBottom: 20 }}>
              <div style={{ fontSize: 14, fontWeight: 800, color: C.navy, textTransform: "uppercase", letterSpacing: 0.8, marginBottom: 18, display: "flex", alignItems: "center", gap: 8 }}>
                <span style={{ fontSize: 16 }}>📉</span> Subject Performance
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
                {scholastic.map((s) => {
                  const isAbs = s.isAbsent;
                  const hasMarks = !isAbs && s.obtained !== null && s.obtained !== undefined;
                  const subPct = hasMarks && s.maxMarks > 0 ? Math.round((Number(s.obtained) / s.maxMarks) * 100) : 0;
                  const { color } = isAbs ? { color: "#94a3b8" } : (hasMarks ? gradeFor(subPct) : { color: "#cbd5e1" });
                  const displayObt = isAbs ? "Absent" : (hasMarks ? `${s.obtained}/${s.maxMarks}` : "—");
                  return (
                    <div key={s.subjectId}>
                      <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 5 }}>
                        <span style={{ fontSize: 13, fontWeight: 600, color: C.navy }}>{s.subjectName}</span>
                        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                          <span style={{ fontSize: 13, fontWeight: 700, color }}>{displayObt}</span>
                          {hasMarks && <span style={{ fontSize: 11, fontWeight: 700, color: "white", background: color, borderRadius: 999, padding: "1px 8px" }}>{gradeFor(subPct).g}</span>}
                        </div>
                      </div>
                      <div style={{ height: 9, background: "#f1f5f9", borderRadius: 999, overflow: "hidden" }}>
                        <div style={{ width: `${subPct}%`, height: "100%", background: color, borderRadius: 999, transition: "width 1s ease" }} />
                      </div>
                      {hasMarks && s.passMarks && (
                        <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 2 }}>
                          <span style={{ fontSize: 10, color: "#94a3b8" }}>Pass mark: {s.passMarks}</span>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* ── DETAILED MARKS TABLE ── */}
          <div style={{ background: "white", borderRadius: 20, boxShadow: "0 4px 24px rgba(0,0,0,0.12)", overflow: "hidden", marginBottom: 20 }}>
            <div style={{ background: C.navy, padding: "14px 24px", fontSize: 13, fontWeight: 800, color: "white", textTransform: "uppercase", letterSpacing: 0.8, display: "flex", alignItems: "center", gap: 8 }}>
              <span>📋</span> Scholastic Marks
            </div>
            <div style={{ overflowX: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead>
                  <tr>
                    {["#", "Subject", "Min", "Max", "Obtained", "Grade", "Remark"].map((h, i) => (
                      <th key={h} style={{ background: "#f8fafc", borderBottom: "2px solid #e2e8f0", padding: "10px 14px", fontSize: 11, fontWeight: 800, color: "#64748b", textTransform: "uppercase", letterSpacing: 0.5, textAlign: i <= 1 ? "left" : "center" }}>
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {scholastic.map((s, i) => {
                    const isAbs = s.isAbsent;
                    const hasMarks = !isAbs && s.obtained !== null && s.obtained !== undefined;
                    const subPct = hasMarks && s.maxMarks > 0 ? Math.round((Number(s.obtained) / s.maxMarks) * 100) : 0;
                    const { g, p, color } = isAbs ? { g: "Ab", p: "Absent", color: "#94a3b8" } : (hasMarks ? gradeFor(subPct) : { g: "—", p: "—", color: "#cbd5e1" });
                    return (
                      <tr key={s.subjectId} style={{ background: i % 2 === 0 ? "#f8fafc" : "white", borderBottom: "1px solid #f1f5f9" }}>
                        <td style={{ padding: "10px 14px", color: "#94a3b8", fontSize: 13, fontWeight: 600 }}>{i + 1}</td>
                        <td style={{ padding: "10px 14px", fontWeight: 700, color: C.navy, fontSize: 13 }}>{s.subjectName}</td>
                        <td style={{ padding: "10px 14px", textAlign: "center", color: "#64748b", fontSize: 13 }}>{s.passMarks || "—"}</td>
                        <td style={{ padding: "10px 14px", textAlign: "center", color: "#64748b", fontSize: 13 }}>{s.maxMarks}</td>
                        <td style={{ padding: "10px 14px", textAlign: "center", fontWeight: 800, fontSize: 15, color: isAbs ? "#94a3b8" : (hasMarks ? C.navy : "#cbd5e1") }}>
                          {isAbs ? "Ab" : (hasMarks ? s.obtained : "—")}
                        </td>
                        <td style={{ padding: "10px 14px", textAlign: "center" }}>
                          <span style={{ display: "inline-block", padding: "2px 10px", borderRadius: 999, fontSize: 12, fontWeight: 800, background: color + "22", color }}>{g}</span>
                        </td>
                        <td style={{ padding: "10px 14px", textAlign: "center", fontSize: 12, color: "#64748b" }}>{p}</td>
                      </tr>
                    );
                  })}
                  {/* Grand total row */}
                  <tr style={{ background: "#FEF3C7", borderTop: "2px solid #F59E0B" }}>
                    <td colSpan={2} style={{ padding: "12px 14px", fontWeight: 900, color: C.navy, fontSize: 13, textTransform: "uppercase", letterSpacing: 0.5 }}>Grand Total</td>
                    <td style={{ padding: "12px 14px", textAlign: "center", fontWeight: 700, color: C.navy }}></td>
                    <td style={{ padding: "12px 14px", textAlign: "center", fontWeight: 700, color: C.navy }}>{result.totalMax}</td>
                    <td style={{ padding: "12px 14px", textAlign: "center", fontWeight: 900, fontSize: 16, color: C.navy }}>{result.totalObtained}</td>
                    <td style={{ padding: "12px 14px", textAlign: "center", fontWeight: 900, color: gradeColor }}>
                      {result.grade}{result.grade === "E" && <span style={{ color: C.red, marginLeft: 4, fontSize: 11 }}>(Fail)</span>}
                    </td>
                    <td style={{ padding: "12px 14px", textAlign: "center", fontWeight: 700, color: gradeColor }}>{pct}%</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          {/* ── CO-SCHOLASTIC ── */}
          {coscho.length > 0 && (
            <div style={{ background: "white", borderRadius: 20, boxShadow: "0 4px 24px rgba(0,0,0,0.12)", overflow: "hidden", marginBottom: 20 }}>
              <div style={{ background: C.navy, padding: "14px 24px", fontSize: 13, fontWeight: 800, color: "white", textTransform: "uppercase", letterSpacing: 0.8, display: "flex", alignItems: "center", gap: 8 }}>
                <span>🎨</span> Co-Scholastic Activities
              </div>
              <div style={{ padding: "16px 24px", display: "flex", flexWrap: "wrap", gap: 12 }}>
                {coscho.map((s) => (
                  <div key={s.subjectId} style={{ background: "#f8fafc", border: "1.5px solid #e2e8f0", borderRadius: 12, padding: "12px 20px", textAlign: "center", minWidth: 110 }}>
                    <div style={{ fontSize: 11, fontWeight: 700, color: "#64748b", textTransform: "uppercase", letterSpacing: 0.4, marginBottom: 6 }}>{s.subjectName}</div>
                    <div style={{ fontSize: 24, fontWeight: 900, color: C.blue }}>{s.gradeValue || "—"}</div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* ── Footer note ── */}
          <div style={{ textAlign: "center", padding: "8px 0" }}>
            <p style={{ fontSize: 12, color: "rgba(255,255,255,0.5)", margin: 0 }}>
              Computer-generated result. For discrepancies, contact your school administration.
            </p>
          </div>
        </div>
      </div>
    );
  }

  /* ════════════════════════════════════════════════════
     STEP: exams
  ════════════════════════════════════════════════════ */
  if (step === "exams") {
    const publishedExams = exams.filter((e) => e.isPublished);
    const withheldExams = exams.filter((e) => !e.isPublished);

    return (
      <div style={{ minHeight: "100vh", background: C.bgGradient }}>
        <div style={{ background: "rgba(255,255,255,0.06)", borderBottom: "1px solid rgba(255,255,255,0.1)", padding: "14px 28px", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <SchoolLogo logoData={schoolInfo?.logoData} size={36} />
            <span style={{ color: "white", fontWeight: 700, fontSize: 16 }}>{schoolInfo?.displayName || schoolInfo?.name || "Student Portal"}</span>
          </div>
          <button onClick={handleSignOut} style={{ background: "rgba(255,255,255,0.12)", color: "white", border: "1px solid rgba(255,255,255,0.25)", borderRadius: 8, padding: "7px 16px", fontSize: 13, fontWeight: 600, cursor: "pointer" }}>
            Sign Out
          </button>
        </div>

        <div style={{ maxWidth: 520, margin: "48px auto", padding: "0 20px" }}>
          <div style={{ background: "white", borderRadius: 20, boxShadow: "0 8px 40px rgba(0,0,0,0.22)", padding: "32px" }}>
            <div style={{ textAlign: "center", marginBottom: 28 }}>
              <div style={{ fontSize: 40, marginBottom: 8 }}>✅</div>
              <h2 style={{ margin: "0 0 6px", color: C.navy, fontSize: 22, fontWeight: 800 }}>Welcome, {studentName}!</h2>
              <p style={{ margin: 0, color: "#6b7280", fontSize: 14 }}>Select an exam to view your result</p>
            </div>

            {error && <div style={{ background: "#fef2f2", border: "1px solid #fecaca", color: C.red, padding: "10px 14px", borderRadius: 8, fontSize: 14, marginBottom: 16 }}>{error}</div>}

            {exams.length === 0 ? (
              <div style={{ textAlign: "center", padding: "32px 0", color: "#9ca3af" }}>
                <div style={{ fontSize: 40, marginBottom: 10 }}>📋</div>
                <div style={{ fontSize: 15, fontWeight: 600 }}>No results available yet</div>
                <div style={{ fontSize: 13, marginTop: 4 }}>Check back later.</div>
              </div>
            ) : (
              <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                {publishedExams.map((exam) => (
                  <button key={exam.id} onClick={() => handleViewResult(exam)} disabled={loading}
                    style={{ width: "100%", background: "#ecfdf5", border: "1.5px solid #6ee7b7", borderRadius: 12, padding: "14px 16px", display: "flex", alignItems: "center", justifyContent: "space-between", cursor: loading ? "not-allowed" : "pointer" }}
                    onMouseEnter={(e) => { e.currentTarget.style.background = "#d1fae5"; }}
                    onMouseLeave={(e) => { e.currentTarget.style.background = "#ecfdf5"; }}>
                    <div style={{ textAlign: "left" }}>
                      <div style={{ fontSize: 15, fontWeight: 700, color: C.navy }}>📝 {exam.name}</div>
                      <div style={{ fontSize: 12, color: C.green, marginTop: 2 }}>{exam.session}</div>
                    </div>
                    <span style={{ fontSize: 18, color: C.green }}>→</span>
                  </button>
                ))}
                {withheldExams.map((exam) => (
                  <div key={exam.id} style={{ background: "#fffbeb", border: "1.5px solid #fcd34d", borderRadius: 12, padding: "14px 16px" }}>
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                      <div>
                        <div style={{ fontSize: 15, fontWeight: 700, color: "#92400e" }}>🔒 Result on Hold</div>
                        <div style={{ fontSize: 13, color: "#78350f", marginTop: 2, fontWeight: 600 }}>{exam.name}</div>
                        <div style={{ fontSize: 12, color: "#a16207", marginTop: 1 }}>{exam.session}</div>
                      </div>
                      <span style={{ fontSize: 11, fontWeight: 700, background: "#fef3c7", color: C.amber, border: "1px solid #fcd34d", borderRadius: 999, padding: "2px 10px" }}>WITHHELD</span>
                    </div>
                    {exam.withheldReason && (
                      <div style={{ marginTop: 10, background: "#fef9c3", border: "1px solid #fde68a", borderRadius: 8, padding: "8px 12px", fontSize: 12, color: "#78350f" }}>
                        <strong>Reason:</strong> {exam.withheldReason}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  /* ════════════════════════════════════════════════════
     STEP: form (lookup)
  ════════════════════════════════════════════════════ */
  if (!schoolIdParam) {
    return (
      <div style={{ minHeight: "100vh", background: C.bgGradient, display: "flex", alignItems: "center", justifyContent: "center", padding: 24 }}>
        <div style={{ background: "white", borderRadius: 16, padding: "40px 36px", maxWidth: 420, textAlign: "center", boxShadow: "0 8px 40px rgba(0,0,0,0.3)" }}>
          <div style={{ fontSize: 48, marginBottom: 12 }}>🔗</div>
          <h2 style={{ color: C.navy, margin: "0 0 10px", fontSize: 20 }}>Invalid Portal Link</h2>
          <p style={{ color: "#6b7280", fontSize: 14, margin: 0 }}>Please use the link provided by your school.</p>
        </div>
      </div>
    );
  }

  return (
    <div style={{ minHeight: "100vh", background: C.bgGradient, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: "24px 16px" }}>
      <div style={{ width: "100%", maxWidth: 460, background: "white", borderRadius: 20, boxShadow: "0 8px 40px rgba(0,0,0,0.32)", overflow: "hidden" }}>
        <div style={{ background: C.navy, padding: "28px 32px 20px", textAlign: "center" }}>
          <div style={{ display: "flex", justifyContent: "center", marginBottom: 10 }}>
            <SchoolLogo logoData={schoolInfo?.logoData} size={56} />
          </div>
          <h1 style={{ margin: "0 0 4px", color: "white", fontSize: 20, fontWeight: 800 }}>
            {schoolInfo?.displayName || schoolInfo?.name || "Student Result Portal"}
          </h1>
          {schoolInfo?.board && <div style={{ color: "#93c5fd", fontSize: 12, marginBottom: 4 }}>{schoolInfo.board}{schoolInfo.affiliationNo ? ` | Affil. No: ${schoolInfo.affiliationNo}` : ""}</div>}
          <p style={{ margin: 0, color: "#bfdbfe", fontSize: 13 }}>Select your class and enter your admission number</p>
        </div>

        <div style={{ padding: "28px 32px 24px" }}>
          <form onSubmit={handleLookup}>
            <FormField label="Class Section">
              <select
                style={{ ...inputStyle, appearance: "none", backgroundImage: "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%236b7280' d='M6 8L1 3h10z'/%3E%3C/svg%3E\")", backgroundRepeat: "no-repeat", backgroundPosition: "right 12px center", paddingRight: 36, color: form.classSectionId ? "#111827" : "#9ca3af" }}
                value={form.classSectionId}
                onChange={(e) => setForm({ ...form, classSectionId: e.target.value })}
                required
              >
                <option value="">-- Select your class --</option>
                {classes.map((c) => (
                  <option key={c.id} value={c.id}>{c.label}</option>
                ))}
              </select>
            </FormField>
            <FormField label="Admission Number">
              <input style={inputStyle} placeholder="e.g. ADM-2024-001" value={form.admissionNo} onChange={(e) => setForm({ ...form, admissionNo: e.target.value })} required />
            </FormField>

            {error && <div style={{ background: "#fef2f2", border: "1px solid #fecaca", color: C.red, padding: "10px 14px", borderRadius: 8, fontSize: 14, marginBottom: 14 }}>{error}</div>}

            <button type="submit" disabled={loading} style={{ width: "100%", background: loading ? "#9ca3af" : C.navy, color: "white", border: "none", borderRadius: 10, padding: "13px 0", fontSize: 15, fontWeight: 700, cursor: loading ? "not-allowed" : "pointer" }}>
              {loading ? "Verifying..." : "View My Result →"}
            </button>
          </form>

          <div style={{ height: 1, background: "#e5e7eb", margin: "20px 0" }} />
          <p style={{ fontSize: 13, color: "#9ca3af", margin: 0, textAlign: "center" }}>
            <Link to="/login" style={{ color: C.blue, fontWeight: 600, textDecoration: "none" }}>School Staff Login →</Link>
          </p>
        </div>
      </div>
    </div>
  );
}
